import React, { useState } from 'react'
import styles from './TagsManagerModal.module.css'
import { useTagsStore } from '../../store/tagsStore'
import { toast } from '../../lib/toast'
import { Pencil, Trash2, Check, X, Tag } from 'lucide-react'
import Button from '../../ui/Button/Button'
import InputModal from '../../ui/inputModal/InputModal'
import SecureInput from '../../ui/SecureInput/SecureInput'

interface TagsManagerModalProps {
    open: boolean
    onClose: () => void
    assignedTags?: import('../../types/types').Tag[]
}

const TagsManagerModal: React.FC<TagsManagerModalProps> = ({ open, onClose, assignedTags }) => {
    const { tags, createTag, deleteTag, renameTag } = useTagsStore(state => state)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [editingName, setEditingName] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [adding, setAdding] = useState(false)
    const [newInlineName, setNewInlineName] = useState('')
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleteName, setDeleteName] = useState('')

    if (!open) return null

    const commitInlineAdd = async () => {
        const name = newInlineName.trim().toLowerCase()
        if (!name) { // Nothing typed – treat as cancel
            cancelInlineAdd()
            return
        }
        setBusy(true)
        setError('')
        try {
            await createTag(name)
            toast.success('Tag created')
            setAdding(false)
            setNewInlineName('')
        } catch (e: any) {
            setError(e.message || 'Failed to create tag')
            toast.error('Failed to create tag')
        } finally {
            setBusy(false)
        }
    }

    const cancelInlineAdd = () => {
        setAdding(false)
        setNewInlineName('')
    }

    const startEdit = (id: number, currentName: string) => {
        setEditingId(id)
        setEditingName(currentName)
        setError('')
    }

    const cancelEdit = () => {
        setEditingId(null)
        setEditingName('')
    }

    const confirmEdit = async () => {
        if (editingId === null) return
        const name = editingName.trim().toLowerCase()
        if (!name) return
        setBusy(true)
        setError('')
        try {
            await renameTag(editingId, name)
            toast.success('Tag renamed')
            cancelEdit()
        } catch (e: any) {
            setError(e.message || 'Failed to rename tag')
            toast.error('Failed to rename tag')
        } finally {
            setBusy(false)
        }
    }

    const performDelete = async (id: number) => {
        setBusy(true)
        setError('')
        try {
            await deleteTag(id)
            toast.success('Tag deleted')
        } catch (e: any) {
            setError(e.message || 'Failed to delete tag')
            toast.error('Failed to delete tag')
        } finally {
            setBusy(false)
            setDeleteId(null)
            setDeleteName('')
        }
    }

    return (
        <div className={styles.backdrop} onMouseDown={onClose}>
            <div className={styles.modal} onMouseDown={e => e.stopPropagation()}>
                <div className={styles.header}>
                    <h2 className={styles.title}>Manage Tags</h2>
                    <button className={styles.closeBtn} onClick={onClose} aria-label="Close">×</button>
                </div>
                <div className={styles.content}>
                    <div className={styles.topBar}>
                        <Button
                            variant={'primary'}
                            size={'lg'}
                            className={styles.addBtn}
                            onClick={() => {
                                setAdding(true); setTimeout(() => {
                                    const el = document.getElementById('new-tag-inline-input');
                                    el?.focus();
                                }, 50)
                            }}
                            disabled={adding || busy}
                        >
                            + Add Tag
                        </Button>
                    </div>
                    {tags.length === 0 && <div className={styles.empty}>No tags yet.</div>}
                    {tags.length > 0 && (
                        <div className={styles.headerRow}>
                            <span className={styles.colName}>Name</span>
                            <span className={styles.colCount}>Count</span>
                            <span className={styles.colActions}>Actions</span>
                        </div>
                    )}
                    <ul className={styles.list}>
                        {adding && (
                            <li className={`${styles.row} ${styles.newRow}`}>
                                <SecureInput
                                    fieldType="text"
                                    label=""
                                    icon={<Tag size={16} />}
                                    value={newInlineName}
                                    placeholder="New tag name"
                                    onChange={(value) => setNewInlineName(value)}
                                    disabled={busy}
                                    className={styles.newInput}
                                />
                                <span className={styles.colCount}></span>
                                <button
                                    className={styles.iconBtn}
                                    onClick={commitInlineAdd}
                                    disabled={busy || !newInlineName.trim()}
                                    aria-label="Save new tag"
                                    title="Save"
                                >
                                    <Check size={16} />
                                </button>
                                <button
                                    className={`${styles.iconBtn} ${styles.iconDanger}`}
                                    onClick={cancelInlineAdd}
                                    disabled={busy}
                                    aria-label="Cancel new tag"
                                    title="Cancel"
                                >
                                    <X size={16} />
                                </button>
                            </li>
                        )}
                        {tags.map(t => (
                            <li key={`${t.id ?? t.name}`} className={styles.row}>
                                {editingId === t.id ? (
                                    <SecureInput
                                        fieldType="text"
                                        label=""
                                        icon={<Tag size={16} />}
                                        value={editingName}
                                        onChange={(value) => setEditingName(value)}
                                        disabled={busy}
                                        className={styles.inputEdit}
                                    />
                                ) : (
                                    <span className={styles.tagName}>{t.name}</span>
                                )}
                                <span className={styles.colCount} style={{ fontSize: 12, color: '#6b7280' }}>{
                                    assignedTags?.some(at => at.name === t.name)
                                        ? `${typeof t.count === 'number' ? t.count : 0} (assigned)`
                                        : typeof t.count === 'number' ? t.count : ''
                                }</span>
                                {editingId === t.id ? (
                                    <>
                                        <button className={styles.saveBtn} onClick={confirmEdit} disabled={busy}><Check size={16} /></button>
                                        <button className={styles.iconBtn} onClick={cancelEdit} disabled={busy}> <X size={16} /></button>
                                    </>
                                ) : (
                                    <>
                                        <button className={styles.iconBtn} onClick={() => startEdit(t.id!, t.name)} disabled={busy || t.id == null} aria-label="Edit tag">
                                            <Pencil size={16} />
                                        </button>
                                        <button className={`${styles.iconBtn} ${styles.iconDanger}`} onClick={() => { setDeleteId(t.id!); setDeleteName(t.name) }} disabled={busy || t.id == null} aria-label="Delete tag">
                                            <Trash2 size={16} />
                                        </button>
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                    {/* Inline add replaced old bottom add form */}
                    {busy && <div className={styles.loading}>Working...</div>}
                    {error && <div className={styles.error}>{error}</div>}
                </div>
                <div className={styles.footer}>
                    <Button className={styles.iconBtn} onClick={onClose}>Close</Button>
                </div>
                <InputModal
                    open={deleteId !== null}
                    title="Delete tag"
                    showInput={false}
                    message={`Delete tag "${deleteName}"? Tools referencing it may break.`}
                    confirmText="Delete"
                    cancelText="Cancel"
                    loading={busy && deleteId !== null}
                    loadingText="Deleting..."
                    onConfirm={() => { if (deleteId != null) performDelete(deleteId) }}
                    onCancel={() => {
                        if (!busy) {
                            setDeleteId(null);
                            setDeleteName('')
                        }
                    }}
                />
            </div>
        </div>
    )
}

export default TagsManagerModal
